import { useState, useRef, useEffect } from "react";

/**
 * Custom React hook to enable draggable functionality for dialog components.
 *
 * @returns {Object} An object containing:
 *   - ref {React.MutableRefObject}: Ref to be attached to the draggable element.
 *   - position {{x: number, y: number}}: Current position of the dialog.
 *   - handleMouseDown {function(MouseEvent): void}: Mouse down handler to initiate dragging.
 *
 * @example
 * const { ref, position, handleMouseDown } = useDraggableDialog();
 * <div
 *   ref={ref}
 *   style={{ left: position.x, top: position.y, position: 'absolute' }}
 *   onMouseDown={handleMouseDown}
 * >
 *   Draggable Dialog
 * </div>
 */
export default function useDraggableDialog() {
  const ref = useRef(null);
  const [position, setPosition] = useState({ x: 400, y: 300 });
  const dragOffset = useRef({ x: 0, y: 0 });

  /**
   * Handles the mouse down event to initiate dragging of a dialog.
   * Only responds to left mouse button clicks.
   * Calculates the offset between the mouse position and the top-left corner of the dialog.
   * Sets up event listeners for mousemove and mouseup to handle the drag operation.
   *
   * @param {MouseEvent} e - The mouse down event object.
   */
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    const rect = ref.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  /**
   * Handles the mouse move event during a drag operation.
   * Calculates the new position of the dialog based on the current mouse coordinates
   * and the initial drag offset, then updates the dialog's position state.
   *
   * @param {MouseEvent} e - The mouse move event object.
   */
  const handleMouseMove = (e) => {
    const newX = e.clientX - dragOffset.current.x;
    const newY = e.clientY - dragOffset.current.y;
    setPosition({ x: newX, y: newY });
  };

  /**
   * Handles the mouse up event by removing the mousemove and mouseup event listeners from the document.
   * This function is typically used to stop dragging behavior when the mouse button is released.
   *
   * @function
   * @returns {void}
   */
  const handleMouseUp = () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  return {
    ref,
    position,
    handleMouseDown,
  };
}
